/**
 * ActionSelection Component
 * 
 * Sixth step in the AlertForm wizard that allows users to select notification methods
 * for the alert when it's triggered.
 */

import React, { useState, useEffect } from 'react';
import { useAlertContext } from '../../../contexts/AlertContext';
import { AlertAction } from '../../../models/AlertModels';
import QWebToggle from '../../common/QWebToggle';
import '../../common/QWebToggleStyleLoader';

/**
 * ActionSelection component for selecting notification methods
 * 
 * @returns {JSX.Element} The ActionSelection component
 */
function ActionSelection() {
  const { formState, updateFormState } = useAlertContext();
  const [selectedActions, setSelectedActions] = useState(formState.alert.actions || [AlertAction.NOTIFICATION]);
  const [validationErrors, setValidationErrors] = useState({});
  
  /**
   * Handle action selection
   * 
   * @param {string} action - The action to toggle
   */
  const handleActionSelect = (action) => {
    let updatedActions;
    
    if (selectedActions.includes(action)) {
      // Remove action if already selected, but don't allow removing all actions
      if (selectedActions.length > 1) {
        updatedActions = selectedActions.filter(a => a !== action);
      } else {
        return; // Don't allow removing the last action
      }
    } else {
      // Add action if not selected
      updatedActions = [...selectedActions, action];
    }
    
    setSelectedActions(updatedActions);
    
    // Update form state
    updateFormState({
      alert: {
        ...formState.alert,
        actions: updatedActions
      }
    });
    
    // Clear validation error if it exists
    if (validationErrors.actions) {
      setValidationErrors({});
    }
  };
  
  /**
   * Validate the selected actions
   * 
   * @returns {boolean} Whether the selection is valid
   */
  const validateActions = () => {
    const errors = {};
    let isValid = true;
    
    if (selectedActions.length === 0) {
      errors.actions = 'At least one action must be selected';
      isValid = false;
    }
    
    setValidationErrors(errors);
    return isValid;
  };
  
  // Validate actions when component mounts or actions change
  useEffect(() => {
    validateActions();
  }, [selectedActions]);
  
  /**
   * Get action description based on action type
   * 
   * @param {string} action - The action type
   * @returns {string} Action description
   */
  const getActionDescription = (action) => {
    switch (action) {
      case AlertAction.NOTIFICATION:
        return 'Send an in-app notification when the alert is triggered.';
      case AlertAction.EMAIL:
        return 'Send an email notification when the alert is triggered.';
      case AlertAction.SLACK:
        return 'Send a Slack message when the alert is triggered.';
      default:
        return 'Action description not available.';
    }
  };
  
  /**
   * Get action icon based on action type
   * 
   * @param {string} action - The action type
   * @returns {string} Icon class
   */
  const getActionIcon = (action) => {
    switch (action) {
      case AlertAction.NOTIFICATION:
        return 'bell';
      case AlertAction.EMAIL:
        return 'envelope';
      case AlertAction.SLACK:
        return 'slack';
      default:
        return 'exclamation-circle';
    }
  };
  
  /**
   * Check if an action is available
   * 
   * @param {string} action - The action type
   * @returns {boolean} Whether the action is available
   */
  const isActionAvailable = (action) => {
    // For now, only in-app notifications are fully implemented
    return action === AlertAction.NOTIFICATION;
  };
  
  return (
    <div className="action-selection-container">
      <h3 className="step-title">Select Notification Methods</h3>
      <p className="step-description">
        Choose how you want to be notified when this alert is triggered.
      </p>
      
      <div className="actions-list">
        {Object.values(AlertAction).map((action) => (
          <div 
            key={action}
            className={`action-item ${selectedActions.includes(action) ? 'selected' : ''} ${!isActionAvailable(action) ? 'disabled' : ''}`}
            onClick={() => isActionAvailable(action) && handleActionSelect(action)}
          >
            <div className="action-icon">
              <i className={`fa fa-${getActionIcon(action)}`}></i>
            </div>
            <div className="action-details">
              <div className="action-name">{action}</div>
              <div className="action-description">{getActionDescription(action)}</div>
              {!isActionAvailable(action) && (
                <div className="action-coming-soon">Coming soon</div>
              )}
            </div>
            <div className="action-checkbox">
              <QWebToggle
                checked={selectedActions.includes(action)}
                onChange={() => isActionAvailable(action) && handleActionSelect(action)}
                onClick={(e) => e.stopPropagation()}
                showLabel={false}
                className="action-toggle"
                aria-label={`Select ${action} notification method`}
                disabled={!isActionAvailable(action)}
              />
            </div>
          </div>
        ))}
      </div>
      
      {validationErrors.actions && (
        <div className="action-selection-error">
          <i className="fa fa-exclamation-triangle"></i>
          {validationErrors.actions}
        </div>
      )}
      
      <div className="action-selection-note">
        <i className="fa fa-info-circle"></i>
        <p>
          In-app notifications are always enabled. Email and Slack notifications will be available in a future update.
        </p>
      </div>
    </div>
  );
}

export default ActionSelection;
